import { useEffect, useState } from 'react';
// MUI
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useColorScheme } from '@mui/material/styles';
// MUI Icons
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import UploadIcon from '@mui/icons-material/UploadOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import BackupIcon from '@mui/icons-material/BackupOutlined';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryOutlined';
import TranslateIcon from '@mui/icons-material/TranslateOutlined';
// Stores
import { useAuth } from '../store/auth';
import { useData } from '../store/data';
// Components
import { CollapsibleSection } from '../components/CollapsibleSection';
import { ImportCsvDialog } from '../components/ImportCsvDialog';
import { SnapshotBanner } from '../components/SnapshotBanner';
import { StatsCards } from '../components/StatsCards';
import { StatusBreakdown } from '../components/StatusBreakdown';
import { TeamBar } from '../components/TeamBar';
import { FilterBar } from '../components/FilterBar';
import { Timeline } from '../components/Timeline';
import { TrendChart } from '../components/TrendChart';
import { ReleaseBlocks } from '../components/ReleaseBlocks';
import { ReleaseFunnel } from '../components/ReleaseFunnel';
import { HistoryChart } from '../components/HistoryChart';
import { EpicDriftTable } from '../components/EpicDriftTable';
import { EpicsTable } from '../components/EpicsTable';
import { OutlierTickets } from '../components/OutlierTickets';
import { DuplicateTickets } from '../components/DuplicateTickets';
import { DependencyBreakdown } from '../components/DependencyBreakdown';
import { CustomerBugs } from '../components/CustomerBugs';
import { ReleaseTickets } from '../components/ReleaseTickets';
import { SprintAnalysis } from '../components/SprintAnalysis';
import { ExpectedBehaviour } from '../components/ExpectedBehaviour';
import { ChecklistView } from '../components/ChecklistView';
import { TicketSearch } from '../components/TicketSearch';
import { ClusterView } from '../components/ClusterView';
import { ReleaseCalDialog } from '../components/ReleaseCalDialog';
import { CapacityDialog } from '../components/CapacityDialog';
import { BackupDialog } from '../components/BackupDialog';
import { SnapshotArchiveDialog } from '../components/SnapshotArchiveDialog';
import { useConfig } from '../store/config';
import { useLocale, useT } from '../i18n';
// Section-Icons
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import PieChartIcon from '@mui/icons-material/PieChartOutlined';
import TimelineIcon from '@mui/icons-material/TimelineOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumnOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AnalyticsIcon from '@mui/icons-material/AnalyticsOutlined';
import TableViewIcon from '@mui/icons-material/TableViewOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import FindReplaceIcon from '@mui/icons-material/FindReplaceOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgentOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunchOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import RateReviewIcon from '@mui/icons-material/RateReviewOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearchOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';

export function Dashboard() {
  const t = useT();
  const s = t.sections;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <TopBar />
      <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
        <Stack spacing={2}>
          <SnapshotBanner />
          <CollapsibleSection
            storageKey="filters"
            title={s.filters.title}
            subtitle={s.filters.subtitle}
            icon={<FilterAltIcon />}
            defaultOpen
          >
            <Stack spacing={1.5}>
              <TeamBar />
              <FilterBar />
            </Stack>
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="stats"
            title={s.stats.title}
            subtitle={s.stats.subtitle}
            icon={<BarChartIcon />}
            defaultOpen
          >
            <StatsCards />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="status"
            title={s.status.title}
            subtitle={s.status.subtitle}
            icon={<PieChartIcon />}
            defaultOpen
          >
            <StatusBreakdown />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="timeline"
            title={s.timeline.title}
            subtitle={s.timeline.subtitle}
            icon={<TimelineIcon />}
            defaultOpen
          >
            <Timeline />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="trend"
            title={s.trend.title}
            subtitle={s.trend.subtitle}
            icon={<TrendingUpIcon />}
            defaultOpen
          >
            <TrendChart />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="columns"
            title={s.columns.title}
            subtitle={s.columns.subtitle}
            icon={<ViewColumnIcon />}
            defaultOpen
          >
            <ReleaseBlocks />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="funnel"
            title={s.funnel.title}
            subtitle={s.funnel.subtitle}
            icon={<TimelineIcon />}
            defaultOpen
          >
            <ReleaseFunnel />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="outliers"
            title={s.outliers.title}
            subtitle={s.outliers.subtitle}
            icon={<WarningAmberIcon />}
            defaultOpen={false}
          >
            <OutlierTickets />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="duplicates"
            title={s.duplicates.title}
            subtitle={s.duplicates.subtitle}
            icon={<FindReplaceIcon />}
            defaultOpen={false}
          >
            <DuplicateTickets />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="customerBugs"
            title={s.customerBugs.title}
            subtitle={s.customerBugs.subtitle}
            icon={<SupportAgentIcon />}
            defaultOpen={false}
          >
            <CustomerBugs />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="releaseTickets"
            title={s.releaseTickets.title}
            subtitle={s.releaseTickets.subtitle}
            icon={<RocketLaunchIcon />}
            defaultOpen={false}
          >
            <ReleaseTickets />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="sprint"
            title={s.sprint.title}
            subtitle={s.sprint.subtitle}
            icon={<SpeedIcon />}
            defaultOpen={false}
          >
            <SprintAnalysis />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="expectedBehaviour"
            title={s.expectedBehaviour.title}
            subtitle={s.expectedBehaviour.subtitle}
            icon={<RateReviewIcon />}
            defaultOpen={false}
          >
            <ExpectedBehaviour />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="checklist"
            title={s.checklist.title}
            subtitle={s.checklist.subtitle}
            icon={<PlaylistAddCheckIcon />}
            defaultOpen={false}
          >
            <ChecklistView />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="search"
            title={s.search.title}
            subtitle={s.search.subtitle}
            icon={<ManageSearchIcon />}
            defaultOpen={false}
          >
            <TicketSearch />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="clusters"
            title={s.clusters.title}
            subtitle={s.clusters.subtitle}
            icon={<AccountTreeIcon />}
            defaultOpen={false}
          >
            <ClusterView />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="deps"
            title={s.deps.title}
            subtitle={s.deps.subtitle}
            icon={<HubIcon />}
            defaultOpen={false}
          >
            <DependencyBreakdown />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="burnup"
            title={s.burnup.title}
            subtitle={s.burnup.subtitle}
            icon={<HistoryIcon />}
            defaultOpen={false}
          >
            <HistoryChart />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="drift"
            title={s.drift.title}
            subtitle={s.drift.subtitle}
            icon={<AnalyticsIcon />}
            defaultOpen={false}
          >
            <EpicDriftTable />
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="epics"
            title={s.epics.title}
            subtitle={s.epics.subtitle}
            icon={<TableViewIcon />}
            defaultOpen={false}
          >
            <EpicsTable />
          </CollapsibleSection>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── AppBar ─────────────────────────────────────────────────────────────

function TopBar() {
  const { me, logout } = useAuth();
  const { mode, setMode } = useColorScheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [relCalOpen, setRelCalOpen] = useState(false);
  const [capaOpen, setCapaOpen]     = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const loadFromBackend = useData(s => s.loadFromBackend);
  const loadConfig      = useConfig(s => s.loadFromBackend);
  const clearAll        = useData(s => s.clearAll);
  const rowCount        = useData(s => s.rows.length);

  const t = useT();
  const locale = useLocale(s => s.locale);
  const toggleLocale = useLocale(s => s.toggle);

  useEffect(() => {
    void loadFromBackend();
    void loadConfig();
  }, [loadFromBackend, loadConfig]);

  async function onClearAll() {
    if (!confirm(t.appBar.confirmClearAll)) return;
    await clearAll();
  }

  const initial = (me?.username?.[0] ?? '?').toUpperCase();
  const themeLabel = mode === 'dark' ? t.appBar.themeLight : t.appBar.themeDark;
  const ThemeIcon = mode === 'dark' ? LightModeIcon : DarkModeIcon;
  const nextLocale = locale === 'de' ? 'EN' : 'DE';
  const langTooltip = `${t.appBar.switchLanguage} → ${nextLocale}`;

  return (
    <>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" component="h1" sx={{ flexGrow: 1, color: 'text.primary' }}>
            {t.appBar.title}
          </Typography>

          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
            {t.appBar.importCsv}
          </Button>

          <Tooltip title={t.appBar.releaseCalendar}>
            <IconButton color="inherit" onClick={() => setRelCalOpen(true)}>
              <CalendarMonthIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t.appBar.capacity}>
            <IconButton color="inherit" onClick={() => setCapaOpen(true)}>
              <GroupsIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t.appBar.backup}>
            <IconButton color="inherit" onClick={() => setBackupOpen(true)}>
              <BackupIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Snapshot-Verlauf">
            <IconButton color="inherit" onClick={() => setArchiveOpen(true)}>
              <HistoryToggleOffIcon />
            </IconButton>
          </Tooltip>

          {rowCount > 0 && (
            <Tooltip title={t.appBar.clearAll}>
              <IconButton color="inherit" onClick={onClearAll}>
                <DeleteSweepIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={langTooltip}>
            <IconButton
              onClick={toggleLocale}
              color="inherit"
              aria-label={langTooltip}
              sx={{ position: 'relative' }}
            >
              <TranslateIcon />
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1,
                  px: '3px',
                  borderRadius: '4px',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  pointerEvents: 'none',
                }}
              >
                {locale.toUpperCase()}
              </Box>
            </IconButton>
          </Tooltip>

          <Tooltip title={themeLabel}>
            <IconButton onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')} color="inherit">
              <ThemeIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t.appBar.account}>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                {initial}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, minWidth: 220 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2">{me?.username}</Typography>
              <Typography variant="caption" color="text.secondary">
                {me?.role === 'admin' ? t.appBar.roleAdmin : t.appBar.roleUser}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => setMenuAnchor(null)} disabled>
              <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t.appBar.profile}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setMenuAnchor(null); void logout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t.appBar.logout}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <ImportCsvDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <ReleaseCalDialog open={relCalOpen} onClose={() => setRelCalOpen(false)} />
      <CapacityDialog   open={capaOpen}   onClose={() => setCapaOpen(false)} />
      <BackupDialog     open={backupOpen} onClose={() => setBackupOpen(false)} />
      <SnapshotArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} />
    </>
  );
}
